import { useState, useEffect, memo, useCallback } from 'react';
import { ButtonLink } from './ui/Button';
import { LANGUAGE_CONFIG } from '../hooks/useLanguage';
import type { Language, HeroTranslations } from '../types';

interface HeroProps {
  t: HeroTranslations;
  lang: Language;
}

/**
 * Screenshots of real systems our agents run inside.
 * Order must match the `systems` array in `translations.ts` (caption + alt text).
 */
interface SystemSlide {
  /** Default capture, used when no language-specific variant exists. */
  src: string;
  /**
   * Language-specific captures, for screens whose *content* is localized
   * (e.g. an RTL Hebrew UI). Falls back to `src` when absent.
   */
  srcByLang?: Partial<Record<Language, string>>;
}

const SYSTEM_SLIDES: SystemSlide[] = [
  { src: '/systems/system-1.webp' },
  { src: '/systems/system-2.webp', srcByLang: { he: '/systems/system-2.he.webp' } },
  { src: '/systems/system-3.webp' },
];

/** Native pixel size of the captures — used for width/height to avoid CLS. */
const SLIDE_WIDTH = 1600;
const SLIDE_HEIGHT = 1000;

/**
 * Hook to defer rendering of non-critical decorative elements
 * Uses requestIdleCallback to wait until browser is idle, improving LCP and TBT
 */
function useDeferredAnimations(delay = 100): boolean {
  const [showAnimations, setShowAnimations] = useState(false);

  useEffect(() => {
    // Check if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      return; // Don't show animations if user prefers reduced motion
    }

    // Use requestIdleCallback to defer animations until browser is idle
    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(
        () => setShowAnimations(true),
        { timeout: 2000 } // Max wait 2 seconds
      );
      return () => window.cancelIdleCallback(idleId);
    } else {
      // Fallback: small delay to let LCP complete first
      const timeoutId = setTimeout(() => setShowAnimations(true), delay);
      return () => clearTimeout(timeoutId);
    }
  }, [delay]);

  return showAnimations;
}

/**
 * Deferred decorative animations component
 * These heavy animations are only rendered after the main content is painted
 */
const DeferredAnimations = memo(function DeferredAnimations() {
  return (
    <>
      {/* Animated light beams */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-[-50%] left-[20%] w-[1px] h-[200%] bg-gradient-to-b from-transparent via-white/20 to-transparent rotate-[20deg] animate-beam"
          aria-hidden="true"
        />
        <div
          className="absolute top-[-50%] left-[40%] w-[2px] h-[200%] bg-gradient-to-b from-transparent via-cyan-300/30 to-transparent rotate-[15deg] animate-beam"
          style={{ animationDelay: '2s' }}
          aria-hidden="true"
        />
        <div
          className="absolute top-[-50%] right-[30%] w-[1px] h-[200%] bg-gradient-to-b from-transparent via-brand-400/20 to-transparent rotate-[-25deg] animate-beam"
          style={{ animationDelay: '4s' }}
          aria-hidden="true"
        />
        <div
          className="absolute top-[-50%] right-[10%] w-[2px] h-[200%] bg-gradient-to-b from-transparent via-white/15 to-transparent rotate-[-15deg] animate-beam"
          style={{ animationDelay: '1s' }}
          aria-hidden="true"
        />
      </div>

      {/* Floating 3D spheres with glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {/* Large glowing orbs */}
        <div className="absolute top-[10%] left-[5%] w-64 h-64 rounded-full bg-gradient-to-br from-cyan-400/40 to-blue-600/20 blur-3xl animate-float-slow" />
        <div
          className="absolute bottom-[10%] right-[5%] w-80 h-80 rounded-full bg-gradient-to-br from-brand/30 to-cyan-500/20 blur-3xl animate-float"
          style={{ animationDelay: '2s' }}
        />
        <div className="absolute top-[50%] left-[50%] w-96 h-96 rounded-full bg-gradient-to-br from-brand-400/20 to-cyan-500/10 blur-3xl animate-pulse-glow" />

        {/* Solid floating spheres with 3D effect */}
        <div className="absolute top-[15%] left-[10%] w-16 h-16 rounded-full bg-gradient-to-br from-white/30 to-white/5 shadow-2xl animate-float border border-white/20 backdrop-blur-sm" />
        <div
          className="absolute top-[25%] right-[15%] w-12 h-12 rounded-full bg-gradient-to-br from-cyan-300/50 to-cyan-500/20 shadow-lg shadow-cyan-500/30 animate-float-slow"
          style={{ animationDelay: '1s' }}
        />
        <div
          className="absolute bottom-[25%] left-[15%] w-20 h-20 rounded-full bg-gradient-to-br from-cyan-300/40 to-cyan-500/10 shadow-lg shadow-cyan-500/20 animate-float"
          style={{ animationDelay: '0.5s' }}
        />
        <div
          className="absolute bottom-[35%] right-[20%] w-10 h-10 rounded-full bg-gradient-to-br from-brand-400/50 to-brand/20 shadow-lg shadow-blue-500/30 animate-float-slow"
          style={{ animationDelay: '1.5s' }}
        />

        {/* Spinning rings */}
        <div className="absolute top-[20%] left-[8%]">
          <div className="w-40 h-40 rounded-full border-2 border-white/10 animate-spin-slow" />
          <div className="absolute inset-2 rounded-full border border-dashed border-cyan-300/20 animate-spin-reverse" />
          <div className="absolute top-1/2 left-0 w-4 h-4 -translate-y-1/2 -translate-x-1/2 rounded-full bg-brand-400 shadow-lg shadow-blue-400/50 animate-pulse" />
        </div>

        <div className="absolute bottom-[20%] right-[10%]">
          <div
            className="w-32 h-32 rounded-full border border-white/15 animate-spin-slow"
            style={{ animationDuration: '20s' }}
          />
          <div
            className="absolute inset-3 rounded-full border border-brand-400/20 animate-spin-reverse"
            style={{ animationDuration: '15s' }}
          />
          <div className="absolute top-0 left-1/2 w-3 h-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-400 shadow-lg shadow-cyan-400/50 animate-pulse" />
        </div>

        {/* Floating squares/diamonds */}
        <div className="absolute top-[40%] left-[3%] w-8 h-8 rotate-45 border-2 border-white/20 animate-gentle-float" />
        <div
          className="absolute top-[60%] right-[5%] w-12 h-12 rotate-12 border-2 border-cyan-300/30 rounded-lg animate-gentle-float-reverse"
          style={{ animationDelay: '1s' }}
        />
        <div
          className="absolute bottom-[40%] left-[25%] w-6 h-6 rotate-45 bg-white/10 backdrop-blur-sm animate-gentle-float"
          style={{ animationDelay: '2s' }}
        />

        {/* Particle dots */}
        <div className="absolute top-[30%] left-[30%] w-2 h-2 rounded-full bg-white shadow-lg shadow-white/50 animate-twinkle" />
        <div
          className="absolute top-[45%] left-[20%] w-1.5 h-1.5 rounded-full bg-cyan-300 shadow-lg shadow-cyan-300/50 animate-twinkle"
          style={{ animationDelay: '0.3s' }}
        />
        <div
          className="absolute top-[55%] right-[25%] w-2 h-2 rounded-full bg-cyan-300 shadow-lg shadow-cyan-300/50 animate-twinkle"
          style={{ animationDelay: '0.6s' }}
        />
        <div
          className="absolute top-[20%] right-[40%] w-1.5 h-1.5 rounded-full bg-brand-400 shadow-lg shadow-blue-400/50 animate-twinkle"
          style={{ animationDelay: '0.9s' }}
        />
        <div
          className="absolute bottom-[30%] left-[40%] w-2 h-2 rounded-full bg-white shadow-lg shadow-white/50 animate-twinkle"
          style={{ animationDelay: '1.2s' }}
        />
        <div
          className="absolute bottom-[20%] right-[35%] w-1.5 h-1.5 rounded-full bg-cyan-300 shadow-lg shadow-cyan-300/50 animate-twinkle"
          style={{ animationDelay: '1.5s' }}
        />
        <div
          className="absolute top-[70%] left-[10%] w-2 h-2 rounded-full bg-cyan-200 shadow-lg shadow-cyan-200/50 animate-twinkle"
          style={{ animationDelay: '1.8s' }}
        />
        <div
          className="absolute top-[35%] right-[8%] w-1.5 h-1.5 rounded-full bg-white shadow-lg shadow-white/30 animate-twinkle"
          style={{ animationDelay: '2.1s' }}
        />

        {/* Ripple effects */}
        <div className="absolute top-[65%] left-[12%]">
          <div className="w-20 h-20 rounded-full border border-white/10 animate-ripple" />
          <div
            className="absolute inset-0 w-20 h-20 rounded-full border border-white/10 animate-ripple"
            style={{ animationDelay: '1s' }}
          />
          <div
            className="absolute inset-0 w-20 h-20 rounded-full border border-white/10 animate-ripple"
            style={{ animationDelay: '2s' }}
          />
        </div>
      </div>
    </>
  );
});

/**
 * Static placeholder elements shown during initial render
 * These are lightweight and don't animate, ensuring fast LCP
 */
const StaticPlaceholder = memo(function StaticPlaceholder() {
  return (
    <>
      {/* Static light beams placeholder */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute top-[-50%] left-[20%] w-[1px] h-[200%] bg-gradient-to-b from-transparent via-white/10 to-transparent rotate-[20deg]" />
        <div className="absolute top-[-50%] right-[30%] w-[1px] h-[200%] bg-gradient-to-b from-transparent via-cyan-300/15 to-transparent rotate-[-25deg]" />
      </div>

      {/* Static decorative elements placeholder */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute top-[10%] left-[5%] w-64 h-64 rounded-full bg-gradient-to-br from-cyan-400/30 to-blue-600/15 blur-3xl" />
        <div className="absolute bottom-[10%] right-[5%] w-80 h-80 rounded-full bg-gradient-to-br from-brand/25 to-cyan-500/15 blur-3xl" />
        <div className="absolute top-[50%] left-[50%] w-96 h-96 rounded-full bg-gradient-to-br from-brand-400/15 to-cyan-500/10 blur-3xl" />
        <div className="absolute top-[15%] left-[10%] w-16 h-16 rounded-full bg-gradient-to-br from-white/20 to-white/5 border border-white/10" />
        <div className="absolute top-[25%] right-[15%] w-12 h-12 rounded-full bg-gradient-to-br from-cyan-300/30 to-cyan-500/10" />
        <div className="absolute bottom-[25%] left-[15%] w-20 h-20 rounded-full bg-gradient-to-br from-cyan-300/25 to-cyan-500/10" />
        <div className="absolute bottom-[35%] right-[20%] w-10 h-10 rounded-full bg-gradient-to-br from-brand-400/30 to-brand/10" />
      </div>
    </>
  );
});

const Hero = memo<HeroProps>(function Hero({ t, lang }) {
  const [currentImage, setCurrentImage] = useState(0);
  const isRTL = LANGUAGE_CONFIG[lang].isRTL;
  
  // Defer heavy animations until after initial paint
  const showAnimations = useDeferredAnimations(100);

  // Auto-cycle images every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % SYSTEM_SLIDES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleImageSelect = useCallback((idx: number) => {
    setCurrentImage(idx);
  }, []);

  return (
    <div className="relative min-h-screen flex items-center overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-brand-600 to-blue-900 animate-gradient-shift" />

      {/* Mesh gradient overlay */}
      <div className="absolute inset-0 opacity-50">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_rgba(56,189,248,0.3)_0%,_transparent_50%)]" />
        <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_rgba(79,108,255,0.4)_0%,_transparent_50%)]" />
        <div className="absolute bottom-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_left,_rgba(34,211,238,0.3)_0%,_transparent_50%)]" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_rgba(79,108,255,0.2)_0%,_transparent_50%)]" />
      </div>

      {/* Decorative elements - deferred for performance */}
      {showAnimations ? <DeferredAnimations /> : <StaticPlaceholder />}

      <div className="relative z-10 w-full px-6 sm:px-12 lg:px-16 xl:px-24 pt-28 pb-20">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Text Content */}
          <div
            className={`w-full lg:w-1/2 text-center ${isRTL ? 'lg:text-right' : 'lg:text-left'}`}
          >
            {/* Badge with glow */}
            <div
              className="inline-block mb-6 animate-fade-in-up"
              style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}
            >
              <span className="relative bg-white/10 backdrop-blur-md px-6 py-2.5 rounded-full text-white font-semibold text-sm tracking-wide inline-flex items-center gap-2 border border-white/20 shadow-lg shadow-white/5">
                <span className="relative w-2 h-2 bg-cyan-400 rounded-full">
                  <span className="absolute inset-0 bg-cyan-400 rounded-full animate-ping" />
                </span>
                {t.badge}
              </span>
            </div>

            {/* Main Headline with glow effect */}
            <h1
              className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-white mb-10 leading-[1.15] opacity-0 animate-slide-up drop-shadow-2xl"
              style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
              lang={lang}
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-200 to-blue-300 drop-shadow-[0_0_30px_rgba(34,211,238,0.3)]">
                {t.headline}
              </span>
            </h1>

            {/* Subheadline */}
            <p
              className="text-xl md:text-2xl lg:text-3xl text-white/90 mb-12 max-w-3xl mx-auto lg:mx-0 leading-relaxed opacity-0 animate-fade-in-up"
              style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}
            >
              {t.subheadline}
            </p>

            {/* CTA Buttons with effects */}
            <div
              className="flex flex-col sm:flex-row gap-5 justify-center lg:justify-start items-center opacity-0 animate-fade-in-up"
              style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}
            >
              <ButtonLink
                href="#contact"
                variant="primary"
                size="lg"
                showArrow
                isRTL={isRTL}
                className="w-full sm:w-auto"
              >
                {t.ctaPrimary}
              </ButtonLink>
              <ButtonLink
                href="#services"
                variant="secondary"
                size="lg"
                className="w-full sm:w-auto"
              >
                {t.ctaSecondary}
              </ButtonLink>
            </div>

          </div>

          {/* System screenshots in a browser frame */}
          <div
            className="w-full lg:w-1/2 opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}
          >
            <div className="w-full max-w-[760px] mx-auto">
              <div className={`relative ${showAnimations ? 'animate-gentle-float' : ''}`}>
                {/* Glow behind the frame */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br from-cyan-400/30 via-brand/20 to-blue-500/30 blur-3xl scale-105 ${showAnimations ? 'animate-pulse-glow' : ''}`}
                  aria-hidden="true"
                />

                {/* Browser window frame with glow border */}
                <div className="relative rounded-2xl p-[3px] bg-gradient-to-br from-white/30 via-white/10 to-white/30 shadow-2xl shadow-black/30">
                  <div className="rounded-[0.9rem] overflow-hidden bg-slate-900">
                    {/* Title bar */}
                    <div
                      className="flex items-center gap-2 px-4 py-3 bg-slate-800/90 border-b border-white/10"
                      aria-hidden="true"
                    >
                      <span className="w-3 h-3 rounded-full bg-red-400/70" />
                      <span className="w-3 h-3 rounded-full bg-amber-400/70" />
                      <span className="w-3 h-3 rounded-full bg-green-400/70" />
                      <div className="flex-1 mx-3 h-5 rounded-md bg-slate-700/60" />
                    </div>

                    {/* Screen content - system screenshots (16:10) */}
                    <div className="relative aspect-[16/10] bg-slate-800">
                      {SYSTEM_SLIDES.map((slide, idx) => (
                        <img
                          key={idx}
                          src={slide.srcByLang?.[lang] ?? slide.src}
                          alt={t.systems?.[idx]?.alt ?? ''}
                          loading={idx === 0 ? 'eager' : 'lazy'}
                          decoding="async"
                          // @ts-expect-error fetchpriority is a valid HTML attribute
                          fetchpriority={idx === 0 ? 'high' : 'low'}
                          width={SLIDE_WIDTH}
                          height={SLIDE_HEIGHT}
                          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${
                            currentImage === idx ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                      ))}

                      {/* Screen reflection */}
                      <div
                        className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </div>

                {/* Decorative glowing elements around the frame */}
                <div
                  className={`absolute -top-8 -right-8 w-32 h-32 bg-cyan-400/20 rounded-full blur-2xl ${showAnimations ? 'animate-pulse' : ''}`}
                  aria-hidden="true"
                />
                <div
                  className={`absolute -bottom-8 -left-8 w-40 h-40 bg-cyan-400/20 rounded-full blur-2xl ${showAnimations ? 'animate-pulse' : ''}`}
                  style={showAnimations ? { animationDelay: '1s' } : undefined}
                  aria-hidden="true"
                />
                <div
                  className={`absolute top-1/2 -right-12 w-24 h-24 bg-brand/20 rounded-full blur-xl ${showAnimations ? 'animate-pulse' : ''}`}
                  style={showAnimations ? { animationDelay: '0.5s' } : undefined}
                  aria-hidden="true"
                />
              </div>

              {/* Caption for the visible system */}
              <p className="mt-6 min-h-[2.5rem] flex items-center justify-center text-center text-sm md:text-base font-medium text-white/80">
                {t.systems?.[currentImage]?.caption}
              </p>

              {/* System selector dots */}
              <div
                className="mt-1 flex justify-center gap-3"
                role="tablist"
                aria-label={t.systemsAria}
              >
                {SYSTEM_SLIDES.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleImageSelect(idx)}
                    role="tab"
                    aria-selected={currentImage === idx}
                    aria-label={t.systems?.[idx]?.caption ?? ''}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      currentImage === idx
                        ? 'bg-white w-8 shadow-lg shadow-white/50'
                        : 'bg-white/40 w-2 hover:bg-white/60'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 animate-fade-in-up"
        style={{ animationDelay: '1.2s', animationFillMode: 'forwards' }}
      >
        <a
          href="#problems"
          className="flex flex-col items-center gap-3 text-white/80 hover:text-white transition-colors group"
          aria-label={t.scrollDownAria}
        >
          <span className="text-sm font-medium tracking-wide">
            {t.scrollDown}
          </span>
          <div className="flex flex-col items-center animate-bounce">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <svg
              className="w-6 h-6 -mt-3 opacity-50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </a>
      </div>
    </div>
  );
});

export default Hero;
